import { Injectable } from '@angular/core';
import { ActiveSecurityContextStateService } from '@auth/active-security-context.state-service';
import { map, Observable } from 'rxjs';
import navigationConfig from '../../../assets/config/navigation.json';
import { environment } from '@env/environment';
import { UserRole } from './models/permissions';
import { NavDrawerRoute, NavDrawer, NavGroup, NavAction } from '../../app-layout/navigation/models/nav-actions.model';

/**
 * Service that manages navigation permissions and filters navigation items based on user roles
 */
@Injectable({ providedIn: 'root' })
export class PermissionsService {
  private readonly config: any = navigationConfig;

  constructor(private activeSecurityContextState: ActiveSecurityContextStateService) {}

  /**
   * Whether the signed-in user's roles satisfy {@link permissionData} using the same rules as the navigation drawer
   * (any listed permission name may match a {@link UserRole.roleName}).
   */
  currentUserMatchesNavigationPermissionData(permissionData: any[] | undefined | null): boolean {
    const userRoles = this.activeSecurityContextState.authState?.userRoles ?? [];
    if (!this.hasPermissionDataArray(permissionData ?? undefined)) {
      return false;
    }
    return this.hasPermissionData(permissionData!, userRoles as UserRole[]);
  }

  getMainNavDrawerRoutes(): Observable<NavDrawerRoute[]> {
    return this.activeSecurityContextState.authState$.pipe(
      map(authState => {
        if (!authState?.userRoles) return [];
        return (this.config.routes as any[])
          .filter(route => this.isRouteActiveAreaEnabled(route) && this.hasDrawerRoutePermission(route, authState.userRoles))
          .map(route => this.mapDrawerRoute(route, authState.userRoles))
          .filter(route => !route.children || route.children.length > 0);
      })
    );
  }

  private routeActiveArea(route: any): string {
    return route.activeArea ?? route.title;
  }

  private isRouteActiveAreaEnabled(route: any): boolean {
    if (route.children?.length) {
      return route.children.some((child: any) =>
        environment.activeAreas.includes(this.routeActiveArea(child))
      );
    }
    return environment.activeAreas.includes(this.routeActiveArea(route));
  }

  private hasPermissionDataArray(permissionData: any[] | undefined): boolean {
    return !!(permissionData && permissionData.length > 0);
  }

  private hasPermissionData(permissionData: any[], userRoles: UserRole[]): boolean {
    return permissionData.some(perm => {
      if (typeof perm === 'string') {
        return !!perm && userRoles.some(role => role.roleName === perm);
      }

      const permissionName = perm?.permissionName;
      const permissionTypes: string[] | undefined = perm?.permissionTypes;

      if (!permissionName) {
        return false;
      }

      const matchingRoles = userRoles.filter(role => role.roleName === permissionName);
      if (matchingRoles.length === 0) {
        return false;
      }

      if (!permissionTypes?.length) {
        return true;
      }

      return matchingRoles.some(role => this.roleSatisfiesPermissionTypes(role, permissionTypes));
    });
  }

  /**
   * When `permissionTypes` is set on navigation or route data, the user's role for that
   * permission name must match one of those access levels (e.g. write-only routes).
   * Write access also satisfies entries that list read access only.
   */
  private roleSatisfiesPermissionTypes(role: UserRole, permissionTypes: string[]): boolean {
    const userPerm = role.permissions;
    if (permissionTypes.includes(userPerm)) {
      return true;
    }
    return permissionTypes.includes('ReadAccess') && userPerm === 'WriteAccess';
  }

  private hasDrawerActionPermission(action: NavAction, userRoles: UserRole[]): boolean {
    if (!this.hasPermissionDataArray(action.permissionData)) return true;
    return this.hasPermissionData(action.permissionData!, userRoles);
  }

  private hasDrawerRoutePermission(route: any, userRoles: UserRole[]): boolean {
    if (this.hasPermissionDataArray(route.permissionData)) {
      return this.hasPermissionData(route.permissionData, userRoles);
    }

    if (route.children?.length) {
      return route.children.some(
        (child: any) => this.isRouteActiveAreaEnabled(child) && this.hasDrawerRoutePermission(child, userRoles)
      );
    }

    if (route.routerLink && !route.drawer?.groups?.length) {
      return this.hasPermissionDataArray(route.permissionData)
        ? this.hasPermissionData(route.permissionData, userRoles)
        : true;
    }

    if (!route.drawer?.groups) return false;

    return route.drawer.groups.some((group: any) =>
      group.items?.some(
        (item: any) =>
          !this.hasPermissionDataArray(item.permissionData) ||
          this.hasPermissionData(item.permissionData, userRoles)
      )
    );
  }

  private mapDrawerRoute(route: any, userRoles: UserRole[]): NavDrawerRoute {
    const mapped: NavDrawerRoute = {
      icon: route.icon,
      title: route.title,
      routerLink: route.routerLink ?? null,
      permissionData: route.permissionData,
      activeArea: route.activeArea,
    };

    if (route.children?.length) {
      mapped.children = route.children
        .filter((child: any) => this.isRouteActiveAreaEnabled(child))
        .filter((child: any) => this.hasDrawerRoutePermission(child, userRoles))
        .map((child: any) => this.mapDrawerRoute(child, userRoles));
    } else if (route.drawer) {
      mapped.drawer = this.mapDrawer(route.drawer, userRoles);
    }

    return mapped;
  }

  private mapDrawer(drawer: any, userRoles: UserRole[]): NavDrawer {
    return {
      title: drawer.title,
      groups: drawer.groups
        .map((group: any) => this.mapDrawerGroup(group, userRoles))
        .filter((group: NavGroup) => group.items.length > 0),
    };
  }

  private mapDrawerGroup(group: any, userRoles: UserRole[]): NavGroup {
    return {
      title: group.title,
      insertHr: group.insertHr,
      collapsable: group.collapsable,
      items: group.items
        .map((item: any) => this.mapDrawerAction(item))
        .filter((item: NavAction) => this.hasDrawerActionPermission(item, userRoles)),
    };
  }

  private mapDrawerAction(item: any): NavAction {
    return {
      title: item.title,
      routerLink: item.routerLink ?? null,
      permissionData: item.permissionData,
      icon: item.icon,
    };
  }
}
